import { autoElement } from '../../../webmodule-common/components/src/webmodule-components';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { fileIsImageFile } from '../../../webmodule-common/other/ui/file-helper-functions';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { getAbsoluteVirtualFilePath } from '../../api/blob-helper';
import { getAssetCdnPath } from '../../../webmodule-common/other/common/assets';
import { getSettingsManager } from '../../supplier/common/supplier-settings';
import { getToolTip } from '../../../webmodule-common/other/tooltip';
import { html } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';

import { isSomething } from '../../../webmodule-common/other/general/nothing';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { tlang } from '../../../webmodule-common/other/language/lang';
import type { PromiseTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import type { SupplierSettings } from '../../api/supplier-api-interface-supplier';
import type { SupplierSettingsManager } from '../../supplier/common/supplier-settings-manager';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';

@autoElement()
export class SettingsLogoBrandingView extends PageControlTabWithIndependantSaving {
  private settingsManager: SupplierSettingsManager = getSettingsManager();
  private dataTracker: DataTracker;
  private dataBinding: DataBinding;
  private newPositiveLogo?: File;
  private newNegativeLogo?: File;
  private positiveLogoName = 'supplier-positive-logo';
  private negativeLogoName = 'supplier-negative-logo';
  // resources
  private newResourceLogo?: File;
  private resourceLogoName = 'resource-logo';

  constructor() {
    super();
    this.pageFragment = 'branding';

    this.dataBinding = new DataBinding(this.ui, this.elementId, input => {
      return `${input}-${this.elementId}`;
    });
    this.dataTracker = new DataTracker(this.dataBinding);

    this.dataTracker.addImageLink(
      this.positiveLogoName,
      () =>
        getAbsoluteVirtualFilePath(
          this.settings.positiveLogoVirtualPath,
          getAssetCdnPath('images/sample-logo-colour.png')
        ),
      value => {
        this.newPositiveLogo = value;
      }
    );

    this.dataTracker.addImageLink(
      this.negativeLogoName,
      () =>
        getAbsoluteVirtualFilePath(
          this.settings.negativeLogoVirtualPath,
          getAssetCdnPath('./assets/images/sample-logo-colour.png')
        ),
      value => {
        this.newNegativeLogo = value;
      }
    );

    this.dataTracker.addImageLink(
      this.resourceLogoName,
      () => getAbsoluteVirtualFilePath(this.settings.resourceLogoVirtualPath, ''),
      value => {
        this.newResourceLogo = value;
      }
    );

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.settings),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('resourceUrl');
    addField('resourceLogoAltText');
  }

  get settings(): SupplierSettings {
    return this.settingsManager.supplierSettings;
  }

  public allowDeletePage(): boolean {
    return false;
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];

    // throw image upload error only when a file has been provided, but it isn't a valid image
    if (this.newPositiveLogo && !fileIsImageFile(this.newPositiveLogo)) {
      errors.push(tlang`Please provide a valid Color Logo`);
    }
    // throw image upload error only when a file has been provided, but it isn't a valid image
    if (this.newNegativeLogo && !fileIsImageFile(this.newNegativeLogo)) {
      errors.push(tlang`Please provide a valid White Logo`);
    }
    const isResourceURLEmpty = isEmptyOrSpace(this.settings.resourceUrl);
    const isResourceDescriptionEmpty = isEmptyOrSpace(this.settings.resourceLogoAltText);
    // if we only have one of the two compulsory resource fields filled out, then log the corresponding validation error
    if (isResourceURLEmpty !== isResourceDescriptionEmpty) {
      if (isResourceURLEmpty) {
        errors.push(tlang`Please provide a Resource URL`);
      }
      if (isResourceDescriptionEmpty) {
        errors.push(tlang`Please provide a Resource label`);
      }
    }
    // throw image upload error only when a file has been provided, but it isn't a valid image
    if (this.newResourceLogo && !fileIsImageFile(this.newResourceLogo)) {
      errors.push(tlang`Please provide a valid Resource Logo`);
    }

    return errors;
  }

  async prepareForSave(): Promise<void> {
    this.dataTracker.applyChangeToValue();
  }

  public internalDataChanged(): boolean {
    return (
      this.settingsManager.changed() || isSomething(this.newNegativeLogo, this.newPositiveLogo, this.newResourceLogo)
    );
  }

  async onEnter(): Promise<void> {
    await this.settingsManager.needsSettings(true);
    this.requestUpdate();
  }

  protected getCaption(): Snippet {
    return tlang`Branding`;
  }

  protected revertChanges(): Promise<boolean> {
    this.newNegativeLogo = undefined;
    this.newNegativeLogo = undefined;
    this.newResourceLogo = undefined;
    this.settingsManager.reset();
    return Promise.resolve(true);
  }

  protected async internalSaveData(): Promise<boolean> {
    // attempt to upload a logo from the given element and get its uploaded file path
    const newPositiveLogo = await this.dataBinding.createVirtualFile(this.newPositiveLogo);
    const newNegativeLogo = await this.dataBinding.createVirtualFile(this.newNegativeLogo);
    const newResoucesImage = await this.dataBinding.createVirtualFile(this.newResourceLogo);

    this.settingsManager.updateInput.base64PositiveLogo = newPositiveLogo?.content ?? null;
    this.settingsManager.updateInput.base64NegativeLogo = newNegativeLogo?.content ?? null;
    this.settingsManager.updateInput.positiveLogoFileName = newPositiveLogo
      ? `positiveBrandingLogo${newPositiveLogo.extension}`
      : null;
    this.settingsManager.updateInput.negativeLogoFileName = newNegativeLogo
      ? `negativeBrandingLogo${newNegativeLogo.extension}`
      : null;

    this.settingsManager.updateInput.base64ResourceLogo = newResoucesImage?.content ?? null;
    this.settingsManager.updateInput.resourceLogoFileName = newResoucesImage
      ? `resourcesLogo${newResoucesImage.extension}`
      : null;

    const result = await this.settingsManager.saveSettings();
    if (result) {
      if (!isAutoSaving()) fireQuickSuccessToast(tlang`Branding saved`);
      this.dataTracker.removeFilesFromEditors();
      this.requestUpdate();
      return true;
    }
    return false;
  }

  protected async bodyTemplate(): PromiseTemplate {
    const form = new FormInputAssistant(this.dataTracker, false, true);

    return html` <div>
      <form id="CompanyBrandingForm" class="form-three-col">
        <webmodule-card class="settings-spec-card settings-spec-card--row">
          <p class="specs-intro">
            ${tlang`Upload your business logo. It is added to documents sent to %%client%%s and %%franchisee%%s (%%estimate%%s, %%invoice%%s, %%purchase-order%%s) and appears at the top of printed PDFs and reports.`}
          </p>
          <ul class="specs-list">
            <li>
              <webmodule-icon library="fa" name="fas-expand"></webmodule-icon>
              <div class="specs-text">
                <span class="specs-label">${tlang`Minimum size`}</span>
                <span class="specs-value">800 &times; 400 px</span>
              </div>
            </li>
            <li>
              <webmodule-icon library="fa" name="fas-star"></webmodule-icon>
              <div class="specs-text">
                <span class="specs-label">${tlang`Ideal size`}</span>
                <span class="specs-value">1600 &times; 800 px</span>
              </div>
            </li>
            <li>
              <webmodule-icon library="fa" name="fas-image"></webmodule-icon>
              <div class="specs-text">
                <span class="specs-label">${tlang`Format`}</span>
                <span class="specs-value">JPG &middot; 300 DPI &middot; ${tlang`white background`}</span>
              </div>
            </li>
          </ul>
          <div class="specs-notes">
            <p>
              <webmodule-icon library="fa" name="fas-circle-info"></webmodule-icon>
              <span>${tlang`Logos with a different aspect ratio are scaled down proportionally to fit.`}</span>
            </p>
          </div>
        </webmodule-card>
        <div class="row branding-wrapper">
          <div class="col-sm-4 form-column">
            <webmodule-card class="settings-upload-card">
              <h4 slot="header" class="settings-upload-card-title">
                ${tlang`Colour Logo`}
                ${getToolTip(tlang`This logo appears on the top left hand corner of your masthead.`)}
              </h4>
              ${form.imagePicker(this.positiveLogoName, tlang`Color Logo`)}
            </webmodule-card>
          </div>
          <div class="col-sm-4 form-column">
            <webmodule-card class="settings-upload-card">
              <h4 slot="header" class="settings-upload-card-title">
                ${tlang`White Logo`}
                ${getToolTip(
                  tlang`This logo appears on printed reports and on the right hand side in the Dealer Portal Footer.`
                )}
              </h4>
              ${form.imagePicker(this.negativeLogoName, tlang`White Logo`)}
            </webmodule-card>
          </div>
          <div class="col-sm-4 form-column">
            <webmodule-card class="settings-upload-card">
              <h4 slot="header" class="settings-upload-card-title">
                ${tlang`Resource Link`}
                ${getToolTip(
                  tlang`This logo appears on the right hand side in the Dealer Portal Footer, linking to the URL provided below.`
                )}
              </h4>
              ${form.imagePicker(this.resourceLogoName, tlang`Resources Logo`, {
                onDeleteEvent: async () => await this.deleteResourceLogo()
              })}
              ${form.text1('resourceUrl', 'URL')} ${form.text1('resourceLogoAltText', 'Resource Alt text')}
            </webmodule-card>
          </div>
        </div>
      </form>
    </div>`;
  }

  protected async deleteResourceLogo() {
    if (!isEmptyOrSpace(this.settings.resourceLogoVirtualPath)) {
      const result = await this.settingsManager.deleteResourceLogo();
      if (result) {
        fireQuickSuccessToast(tlang`Logo deleted`);
      }
    }
    const binder = this.dataTracker.getBinder(this.resourceLogoName);
    if (binder) {
      binder.editorBinder.setValue('');
      this.requestUpdate();
    }
  }
}
